import { useEffect, useRef, useState } from "react";
import { apiFetchStream } from "@/api";
import type { ProgressLine, RoutesResponse } from "@/types";

const INITIAL_COUNT = 10;
const LOAD_STEP = 5;

export interface UsePagedRoutesResult {
  routes: RoutesResponse["routes"];
  unit: RoutesResponse["unit"] | null;
  hasMore: boolean;
  /** True during the initial (first-page) fetch only. */
  loading: boolean;
  /** True during a loadMore() fetch — distinct from `loading` so the UI can
   *  show a small inline spinner instead of the full map loading overlay. */
  loadingMore: boolean;
  progress: ProgressLine | null;
  error: string | null;
  loadMore(): void;
}

function withParam(url: string, param: string): string {
  return `${url}${url.includes("?") ? "&" : "?"}${param}`;
}

/**
 * Fetches routes a page at a time (server-side count/cursor pagination, not
 * a client-side slice of an already-fully-fetched day — see the migration
 * plan). `baseUrl` should already carry `date`/`site` (e.g.
 * `/api/routes/stream?date=...&site=...`); this hook appends `count`/`cursor`.
 * Resets and re-fetches the first page whenever `baseUrl` changes.
 */
export function usePagedRoutes(baseUrl: string | null): UsePagedRoutesResult {
  const [routes, setRoutes] = useState<RoutesResponse["routes"]>([]);
  const [unit, setUnit] = useState<RoutesResponse["unit"] | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(baseUrl != null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [progress, setProgress] = useState<ProgressLine | null>(null);
  const [error, setError] = useState<string | null>(null);
  const gen = useRef(0);

  useEffect(() => {
    const myGen = ++gen.current;
    setRoutes([]);
    setUnit(null);
    setHasMore(false);
    setNextCursor(null);
    setLoadingMore(false);
    setProgress(null);
    setError(null);

    if (baseUrl == null) {
      setLoading(false);
      return;
    }
    setLoading(true);

    apiFetchStream<RoutesResponse>(withParam(baseUrl, `count=${INITIAL_COUNT}`), (p) => {
      if (gen.current === myGen) setProgress(p);
    })
      .then((data) => {
        if (gen.current !== myGen) return;
        setRoutes(data.routes);
        setUnit(data.unit);
        setHasMore(data.hasMore);
        setNextCursor(data.nextCursor);
        setLoading(false);
        setProgress(null);
      })
      .catch((e: unknown) => {
        if (gen.current !== myGen) return;
        setError((e as Error).message);
        setLoading(false);
      });
  }, [baseUrl]);

  const loadMore = (): void => {
    if (baseUrl == null || !hasMore || !nextCursor || loading || loadingMore) return;
    const myGen = gen.current;
    setLoadingMore(true);
    const url = withParam(withParam(baseUrl, `count=${LOAD_STEP}`), `cursor=${encodeURIComponent(nextCursor)}`);
    apiFetchStream<RoutesResponse>(url, () => {})
      .then((data) => {
        if (gen.current !== myGen) return;
        setRoutes((prev) => [...prev, ...data.routes]);
        setHasMore(data.hasMore);
        setNextCursor(data.nextCursor);
        setLoadingMore(false);
      })
      .catch((e: unknown) => {
        if (gen.current !== myGen) return;
        setError((e as Error).message);
        setLoadingMore(false);
      });
  };

  return { routes, unit, hasMore, loading, loadingMore, progress, error, loadMore };
}
